import React, { useState, useEffect } from "react";

import _ from 'lodash';
import cx from 'classnames';

import { useTransition, animated } from "@react-spring/web";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { addLearnerToCourse, invalidateAddLearnersState } from "src/redux/actions/coaching/learners";

import Slider from "src/components/slider";
import Actions from "src/components/actionbar";
import InfiniteScroll from "src/components/infiniteScroll";
import { Checkbox } from "src/components/input";
import Thumbnail from "src/components/thumbnail";
import GroupThumbnail from "../groupThumbnail";
import Button from "src/components/button";

import styles from "./learners.module.scss";
import { LearnerPlaceholder } from ".";
import { invalidateLearnerSearchResults, searchLearners } from "src/redux/actions/search";
import { Course, CourseSummary } from "src/types/coaching/courses";
import Tooltip from "src/components/tooltip";
import LinesEllipsis from "react-lines-ellipsis";
import { Simulation, SimulationSummary } from "src/types/coaching/simulation";

interface AddLearnersDialogProps {
  show: boolean;
  course?: Course | Simulation | CourseSummary | SimulationSummary;
  onFinish: () => void;
  onClose: () => void;
}

interface BaseEntity {
  id: string;
  entityType: string;
}

export const AddLearnersDialog: React.FC<AddLearnersDialogProps> = ({ show, course, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id || course?.id || "";

  const { loading, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'search.learners');
    const loading = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  const adding = useSelector((state: any) =>
    _.get(state, "learners.add.loading", false),
  );
  const added = useSelector((state: any) =>
    _.get(state, "learners.add.loaded", false),
  );

  const [selectAll, setSelectAll] = useState(false);
  const [selectedLearners, setSelectedLearners] = useState<BaseEntity[]>([]);
  const [query, setQuery] = useState<string | null>("");

  const fetchLearnersForPage = (query, page) => {
    const request = {
      query: query || "",
      page: page,
      limit: 20,
    };

    dispatch(searchLearners(request));
  };

  const onSelect = (learner: BaseEntity) => {
    let items = [...selectedLearners];

    const selected = _.find(selectedLearners, { id: learner.id, entityType: learner.entityType });

    if (selected) {
      items = items.filter((item) => !(item.id === selected.id && item.entityType === selected.entityType));
    } else {
      items = [...items, {
        id: learner.id,
        entityType: learner.entityType
      }];
    }

    setSelectedLearners(items);
  };

  const onSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedLearners([]);
  };

  const handleAddLearners = () => {
    const request = {
      id: id,
      allUsers: selectAll,
      learners: selectedLearners,
    };

    dispatch(addLearnerToCourse(request));
  };

  const handleLoadMoreLearners = () => {
    if (nextPage) {
      fetchLearnersForPage(query, nextPage);
    }
  };

  const handleQueryChange = (query: any) => {
    setQuery(query || "");
  };

  const handleClose = () => {
    setSelectedLearners([]);
    setSelectAll(false);
    onClose();
  }

  useEffect(() => {
    fetchLearnersForPage(query, 0);
  }, [query]);

  useEffect(() => {
    if (added) {
      dispatch(invalidateAddLearnersState());
      onFinish();
    }
  }, [added]);

  return (
    <Slider.Dialog
      className={styles.addLearner}
      show={show}
      onClose={onClose}
      width={"23.75rem"}
    >
      <Slider.Header
        title={"adding learners"}
        message="Select learners for the course you're creating"
      />
      <Slider.Body className={styles.body}>
        <div className={styles.selectAll}>
          <span className={styles.heading}>
            Assign to all users & user groups
          </span>
          <Checkbox checked={selectAll} onChange={() => onSelectAll()} />
        </div>
        <div className={styles.inputContainer}>
          <Actions.Search query={query} onSearch={handleQueryChange} />
        </div>

        <div className={cx(styles.container)}>
          <InfiniteScroll
            className={styles.learnersList}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreLearners}
            loader={() => {
              return _.times(_.size(learners) === 0 || currentPage == 0 ? 4 : 1, (index) => (
                <LearnerPlaceholder key={index} />
              ));
            }}
          >
            {_.size(learners) === 0 && !loading && (
              <span className={styles.noRecordsFound}>
                No results found
              </span>
            )}

            {_.size(learners) > 0 &&
              <LearnersList
                selectAll={selectAll}
                learners={learners}
                selectedLearners={selectedLearners}
                onSelect={onSelect}
              />
            }
          </InfiniteScroll>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          disabled={adding || ( _.size(selectedLearners) === 0 && !selectAll)}
          icon="paperflite-tick"
          onClick={() => handleAddLearners()}
        >
          {adding ? "Adding" : "Add"}
        </Button>
        <Button disabled={adding} variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface LearnersListProps {
  selectAll: boolean;
  learners: any[];
  selectedLearners: any[];
  // eslint-disable-next-line no-unused-vars
  onSelect: (learner: BaseEntity) => void;
}

export const LearnersList: React.FC<LearnersListProps> = ({
  selectAll,
  learners,
  selectedLearners,
  onSelect,
}) => {

  const [showTooltip, setShowTooltip] = useState(false);

  const handleSelect = (learner) => {
    if (!selectAll) {
      onSelect(learner);
    }
  }

  return (
    <>{learners.map((user, index) => {
      const selected = _.find(selectedLearners, { id: user.id, entityType: user.entityType }) != null;
      return (
        <div key={index} className={styles.learner} onClick={() => handleSelect(user)}>
          <div className={styles.details}>
            {user.entityType == "user_group" && (
              <GroupThumbnail
                icons={user.icons}
              />
            )}

            {user.entityType == "user" && (
              <Thumbnail
                media={user.icon}
                defaultImage="/images/assets/default_asset.jpg"
                className={styles.icon}
              />
            )}

          <Tooltip content={user.entityType === `user_group` ? `${user.name}` : `${user.firstName} ${user.lastName}`} placement="top" disabled={!setShowTooltip || user?.name?.length < 20}>
              <LinesEllipsis
                className={styles.name}
                text={user.entityType === `user_group` ? `${user.name}` : `${user.firstName} ${user.lastName}`}
                maxLine="1"
                basedOn="letters"
                ellipsis="..."
                onReflow={({ clamped }) => setShowTooltip(clamped)}
              />
          </Tooltip>
          </div>

          <Checkbox disabled={selectAll} checked={selected || selectAll} onChange={() => onSelect(user)} />
        </div>
      )
    })}
    </>
  );
};

interface LearnersSearchResultProps {
  className?: string;
  query?: string | null;
  selectedLearners: any[];
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const LearnersSearchResult: React.FC<LearnersSearchResultProps> = ({ className, query, selectedLearners, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'search.learners');
    const loading = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchLearnersForPage(0);

    return () => {
      dispatch(invalidateLearnerSearchResults());
    }
  }, [query]);

  const searchLearnersForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        page: page,
        limit: 20
      };

      dispatch(searchLearners(request));
    }
  }

  const handleLoadMore = () => {
    if (nextPage) {
      searchLearnersForPage(nextPage);
    }
  }

  return (
    <InfiniteScroll className={cx(styles.searchResults, className)}
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={handleLoadMore}
      loader={() => {
        return _.times(_.size(learners) === 0 || currentPage == 0 ? 4 : 1, (index) => (
          <LearnerPlaceholder key={index} />
        ));
      }}
    >
      {_.size(learners) === 0 && !loading && (
        <span className={styles.noRecordsFound}>No results found</span>
      )}

      {_.size(learners) > 0 &&
        <LearnersList
          selectAll={false}
          learners={learners}
          selectedLearners={selectedLearners}
          onSelect={onSelection}
        />
      }
    </InfiniteScroll>
  );
}